import { queryOptions, useQuery } from '@tanstack/react-query';

import { TimeRange, ApiResponse, QueryOptions } from '@cast/shared-types';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';
import { useRoxy } from 'features/db-optimization/context/RoxyContext';

const apiFn = apiClient.dbo.getCacheGroupPerformanceSummary;

const DBO_CACHE_PERFORMANCE_METRICS_SUMMARY_QUERY_KEY =
  'dbo-cache-performance-metrics-summary';

export type CacheGroupPerformanceMetricsSummaryQueryData = ApiResponse<
  typeof apiFn
>['metrics'];

export type CacheGroupPerformanceMetricsSummaryQueryArgs<SELECT> = QueryOptions<
  CacheGroupPerformanceMetricsSummaryQueryData,
  SELECT
> & {
  key: string;
  cacheGroupId?: string;
  cacheConfigId?: string;
  endpointName?: string;
  username?: string;
  getRange: () => TimeRange;
};

const getCacheGroupPerformanceMetricsSummaryQueryKey = (
  params: {
    key?: string;
    cacheGroupId?: string;
    cacheConfigId?: string;
    endpointName?: string;
    username?: string;
    roxy?: boolean;
  } = {}
) =>
  [
    DBO_CACHE_PERFORMANCE_METRICS_SUMMARY_QUERY_KEY,
    params.key,
    params.cacheGroupId,
    params.cacheConfigId,
    params.endpointName,
    params.username,
  ] as const;

const getCacheGroupPerformanceMetricsSummaryQueryOptions = ({
  key,
  cacheGroupId,
  cacheConfigId,
  endpointName,
  username,
  getRange,
  enabled,
  roxy,
}: Omit<CacheGroupPerformanceMetricsSummaryQueryArgs<unknown>, 'select'> & {
  roxy?: boolean;
}) =>
  queryOptions({
    queryKey: getCacheGroupPerformanceMetricsSummaryQueryKey({
      key,
      cacheGroupId,
      cacheConfigId,
      endpointName,
      username,
    }),
    queryFn: async () => {
      const range = getRange();
      const { data } = await apiFn({
        id: cacheGroupId!,
        databaseId: cacheConfigId,
        endpointName,
        username,
        metricsRangeStart: range.from,
        metricsRangeEnd: range.to,
        roxy,
      });
      return data.metrics;
    },
    enabled: makeIsEnabled(!!cacheGroupId, enabled),
  });

export const useCacheGroupPerformanceMetricsSummaryQuery = <
  SELECT = CacheGroupPerformanceMetricsSummaryQueryData
>({
  key,
  cacheGroupId,
  cacheConfigId,
  endpointName,
  username,
  getRange,
  enabled,
  select,
  refetchInterval,
}: CacheGroupPerformanceMetricsSummaryQueryArgs<SELECT>) => {
  const roxy = useRoxy();

  return useQuery({
    ...getCacheGroupPerformanceMetricsSummaryQueryOptions({
      key,
      cacheGroupId,
      cacheConfigId,
      endpointName,
      username,
      getRange,
      enabled,
      roxy,
    }),
    select,
    refetchInterval,
  });
};
